// ─── Edge-swipe tools drawer ────────────────────────────────────────────────
// On phones/tablets (<1024px) the album editor's 7 page tools — Texte, Photo,
// Rectangle, Ellipse, Mise en page, Thèmes, Modèles — live in a drawer that
// slides in from the LEFT EDGE. A thin always-visible handle makes it
// discoverable; a left-edge swipe opens it.
//
// The editor already binds a one-finger horizontal swipe to the WHOLE viewport
// (spread navigation — flip to the next/previous spread). If the drawer also
// listened everywhere the two would fight. So the drawer only claims touches
// that START inside a thin left-edge strip; everything past it keeps today's
// spread-nav / canvas behaviour untouched.
//
// These helpers are pure (no DOM, no React) so the gesture decision is unit-
// tested in isolation, the same way `uploadDedupe.ts` is.

/** Width of the left-edge "grab" strip, in CSS px. A touch starting here is the
 *  drawer's; a touch starting past it belongs to the canvas / spread-nav. */
export const EDGE_ZONE_PX = 24;

/** Horizontal distance a drag must cover before it commits to open/close, so a
 *  stray tap or a vertical scroll near the edge doesn't flick the drawer. */
export const OPEN_THRESHOLD_PX = 48;
export const CLOSE_THRESHOLD_PX = 48;

/** Did this touch begin inside the left-edge grab strip? Only edge touches are
 *  allowed to drive the drawer (and are suppressed from spread navigation). */
export function isEdgeZoneTouch(clientX: number, edgeZonePx: number = EDGE_ZONE_PX): boolean {
  return clientX <= edgeZonePx;
}

/**
 * For a drag that STARTED at the left edge while the drawer is closed: should it
 * open? Open on a rightward (dx > 0), mostly-horizontal drag past the threshold.
 * Vertical-dominant drags are left alone so the page can still scroll.
 */
export function shouldOpenDrawer(dx: number, dy: number, threshold: number = OPEN_THRESHOLD_PX): boolean {
  return dx >= threshold && Math.abs(dx) > Math.abs(dy);
}

/**
 * While the drawer is OPEN: should a swipe-back dismiss it? Close on a leftward
 * (dx < 0), mostly-horizontal drag past the threshold.
 */
export function shouldCloseDrawer(dx: number, dy: number, threshold: number = CLOSE_THRESHOLD_PX): boolean {
  return dx <= -threshold && Math.abs(dx) > Math.abs(dy);
}
